import { Button, Checkbox, Form, Modal, Slider, Spin } from "antd";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { FormDropdownField, FormTextFormField } from "../../elements";
import { X } from "@phosphor-icons/react";

export const LayerFilterModal = ({ onFilterChange, data, open, closeModal }) => {
    const [processing, setProcessing] = useState(false);

    const {
        control,
        formState: { errors },
        handleSubmit,
        setValue,
        reset,
    } = useForm({
        defaultValues: data?.filters?.[0] || {}, // Default values from filters
    });

    useEffect(() => {
        if (data?.filters?.[0]) {
            Object.keys(data?.filters?.[0]).forEach((key) => {
                setValue(key, data?.filters?.[0][key]); // Dynamically set form field values
            });
        }
    }, [data, setValue]);

    useEffect(() => {
        if (open) {
            reset(data?.filters?.[0] || {}); // Reset form with default values
        }
    }, [open, data, reset]);

    const save = async (userFormData) => {
        setProcessing(true);
        await onFilterChange(data?.key, userFormData);
        setProcessing(false);
        closeModal();
    };

    return (
        <Modal
            open={open}
            title="Filter"
            closeIcon={<X size={16} weight="bold" />}
            onCancel={closeModal}
            footer={[
                <Button
                    key="submit"
                    type="primary"
                    disabled={processing}
                    onClick={handleSubmit(save)}
                    block
                >
                    Submit
                </Button>,
            ]}
        >
            <Form layout="vertical">
                <Spin spinning={processing} fullscreen />
                <div className="row g-3">
                    {data?.filter?.map((field, index) => {
                        const { key, type, label, options, placeholder, required } = field;

                        switch (type) {
                            case "search":
                                return (
                                    <div className="col-12" key={index}>
                                        <FormTextFormField
                                            control={control}
                                            errors={errors?.[key]}
                                            name={key}
                                            required={required}
                                            label={key || "Search Field"}
                                        />
                                    </div>
                                );

                            case "dropdown":
                                return (
                                    <div className="col-12" key={index}>
                                        <FormDropdownField
                                            label={key || "Dropdown"}
                                            options={options?.map((ele) => ({
                                                label: ele,
                                                value: ele,
                                            }))}
                                            name={key}
                                            control={control}
                                            placeholder={placeholder || "Select an option"}
                                        />
                                    </div>
                                );

                            case "slider":
                                return (
                                    <div className="col-12" key={index}>
                                        <Form.Item
                                            label={label}
                                            name={key}
                                        >
                                            <Controller
                                                name={key}
                                                control={control}
                                                render={({ field }) => (
                                                    <>
                                                    <label>{key}</label>
                                                    <Slider
                                                        {...field}
                                                        min={options?.[0]}
                                                        max={options?.[1]}
                                                        range
                                                        defaultValue={field.value || [options?.[0], options?.[1]]} // Use value or fallback
                                                        onChange={(value) => field.onChange(value)}
                                                    />
                                                    </>
                                                )}
                                            />
                                        </Form.Item>
                                    </div>
                                );

                            case "checkbox":
                                return (
                                    <div className="col-12" key={index}>
                                        <Controller
                                            name={key}
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    {...field}
                                                    checked={field.value || false} // Handle checkbox value
                                                    onChange={(e) =>
                                                        field.onChange(e.target.checked)
                                                    }
                                                >
                                                    {label || "Checkbox Field"}
                                                </Checkbox>
                                            )}
                                        />
                                    </div>
                                );

                            case "checklist":
                                return (
                                    <div className="col-12" key={index}>
                                        <Form.Item
                                            name={key}
                                            label={key || "Checklist"}
                                            required={required}
                                        >
                                            <Controller
                                                name={key}
                                                control={control}
                                                render={({ field }) => (
                                                    <Checkbox.Group
                                                        {...field}
                                                        value={field.value || []}
                                                        options={options.map((option) => ({
                                                            label: option,
                                                            value: option,
                                                        }))}
                                                        onChange={(checkedValues) =>
                                                            field.onChange(checkedValues)
                                                        }
                                                    />
                                                )}
                                            />
                                        </Form.Item>
                                    </div>
                                );

                            default:
                                return null;
                        }
                    })}
                </div>
            </Form>
        </Modal>
    );
};
